/**
 * ТЗ 0106, этап 2 — путь лида с именем: словари и чистые помощники.
 *
 * Сервер отдаёт события плоским списком по точному времени (`leadJourney`):
 * `kind` и `tone` — коды из фиксированного перечня, `title`/`detail` —
 * ДАННЫЕ (путь страницы, имя этапа, номер договора, сумма, код события
 * карточки), а не фраза: подпись каждого вида строит экран. `meta` —
 * подробности вида в camelCase (`JSONString`).
 *
 * Модуль без React, Apollo и i18n — всё здесь проверяется юнит-тестами.
 */

import moment from 'moment';

import { warsawDayKey } from 'core/utils/zoomTime';

/** Вид события пути (`LeadJourneyEvent.kind`). */
export const LEAD_JOURNEY_KINDS = [
  'visit',
  'page',
  'form',
  'lead_created',
  'stage',
  'touch',
  'card_event',
  'contract_sent',
  'contract_opened',
  'contract_signed',
  'sale',
  'payment',
] as const;
export type LeadJourneyKind = typeof LEAD_JOURNEY_KINDS[number];

/** Тон события (`LeadJourneyEvent.tone`). */
export const LEAD_JOURNEY_TONES = [
  'neutral',
  'info',
  'success',
  'warning',
  'danger',
] as const;
export type LeadJourneyTone = typeof LEAD_JOURNEY_TONES[number];

/** Откуда событие формы: сессия сайта или заявка с сайта. */
export const LEAD_JOURNEY_FORM_ORIGINS = ['session', 'site_lead'] as const;

/** Откуда смена этапа: Kommo или перенос из ERP. */
export const LEAD_JOURNEY_STAGE_ORIGINS = ['kommo', 'erp'] as const;

/** Тон тега ERP (`components/constants/tagTones`). */
type TagTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info' | 'gold';

const TONE_TO_TAG: Readonly<Record<LeadJourneyTone, TagTone>> = {
  neutral: 'neutral',
  info: 'info',
  success: 'ok',
  warning: 'warn',
  danger: 'danger',
};

/** Тон события → тон тега; незнакомый — нейтральный. */
export const leadJourneyTagTone = (tone: string | null | undefined): TagTone =>
  TONE_TO_TAG[tone as LeadJourneyTone] ?? 'neutral';

/** Ключ подписи вида (`leads.journey.kinds.<kind>`) или `null` — незнакомый. */
export const leadJourneyKindKey = (kind: string | null | undefined) =>
  kind && (LEAD_JOURNEY_KINDS as readonly string[]).includes(kind)
    ? `leads.journey.kinds.${kind}`
    : null;

/** Ключ подписи тона (`leads.journey.tones.<tone>`) или `null`. */
export const leadJourneyToneKey = (tone: string | null | undefined) =>
  tone && (LEAD_JOURNEY_TONES as readonly string[]).includes(tone)
    ? `leads.journey.tones.${tone}`
    : null;

export type ILeadJourneyMeta = Record<string, unknown>;

/**
 * `meta` с провода: `JSONString` приходит строкой, но кэш или тест могут
 * отдать уже объект. Мусор — пустой объект: событие без подробностей лучше
 * упавшей вкладки.
 */
export const parseLeadJourneyMeta = (raw: unknown): ILeadJourneyMeta => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as ILeadJourneyMeta;
  }
  if (typeof raw !== 'string' || !raw.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as ILeadJourneyMeta)
      : {};
  } catch {
    return {};
  }
};

/** Строка из `meta` или `null`. */
export const metaString = (
  meta: ILeadJourneyMeta,
  key: string,
): string | null => {
  const value = meta[key];
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

/** Число из `meta` или `null`. */
export const metaNumber = (
  meta: ILeadJourneyMeta,
  key: string,
): number | null => {
  const value = meta[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(+value)) {
    return Number(value);
  }
  return null;
};

/**
 * UTM-метки из `meta.utm` (`{utm_source: …}` или `{source: …}`) — пары
 * «метка → значение» в порядке source, medium, campaign, content, term.
 */
export const LEAD_JOURNEY_UTM_KEYS = [
  'source',
  'medium',
  'campaign',
  'content',
  'term',
] as const;

export interface ILeadJourneyUtm {
  key: typeof LEAD_JOURNEY_UTM_KEYS[number];
  value: string;
}

export const leadJourneyUtm = (meta: ILeadJourneyMeta): ILeadJourneyUtm[] => {
  const raw = meta.utm;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const utm = raw as Record<string, unknown>;
  return LEAD_JOURNEY_UTM_KEYS.flatMap((key) => {
    const value = utm[`utm_${key}`] ?? utm[key];
    return typeof value === 'string' && value.trim()
      ? [{ key, value: value.trim() }]
      : [];
  });
};

/** Длительность в секундах словами экрана: `95` → `1:35`, `3725` → `1:02:05`. */
export const formatJourneyDuration = (
  seconds: number | null | undefined,
): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds))
    return '';
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

/** Событие пути после разбора `meta`. */
export interface ILeadJourneyEvent {
  at: string;
  kind: string;
  title: string;
  detail: string | null;
  channel: string | null;
  tone: string;
  meta: ILeadJourneyMeta;
}

export interface ILeadJourneyRawEvent {
  at: string;
  kind: string;
  title: string;
  detail?: string | null;
  channel?: string | null;
  tone: string;
  meta?: unknown;
}

export const toLeadJourneyEvents = (
  raw: ReadonlyArray<ILeadJourneyRawEvent>,
): ILeadJourneyEvent[] =>
  raw.map((event) => ({
    at: event.at,
    kind: event.kind,
    title: event.title || '',
    detail: event.detail ?? null,
    channel: event.channel ?? null,
    tone: event.tone,
    meta: parseLeadJourneyMeta(event.meta),
  }));

/**
 * Строка таймлайна: событие и — у визита — его страницы. Страницы сворачиваются
 * внутрь визита: сессия на двадцать страниц иначе съедала бы весь путь.
 */
export interface ILeadJourneyItem {
  event: ILeadJourneyEvent;
  pages: ILeadJourneyEvent[];
}

/** День таймлайна: ключ `YYYY-MM-DD` (по Варшаве) и строки дня. */
export interface ILeadJourneyDay {
  day: string;
  items: ILeadJourneyItem[];
}

const byTime = (a: ILeadJourneyEvent, b: ILeadJourneyEvent) =>
  moment(a.at).valueOf() - moment(b.at).valueOf();

/**
 * События → дни → строки. Страница уходит внутрь последнего визита до неё
 * (сервер шлёт страницы сессии после её визита, но между ними может лечь
 * форма или создание лида — они идут своими строками). Страница без визита
 * (сессия за пределом выборки) — своей строкой. Порядок — по времени,
 * стабильный: равное время сохраняет порядок сервера (по виду).
 */
export const groupLeadJourney = (
  events: ReadonlyArray<ILeadJourneyEvent>,
): ILeadJourneyDay[] => {
  const sorted = events
    .map((event, index) => ({ event, index }))
    .sort((a, b) => byTime(a.event, b.event) || a.index - b.index)
    .map(({ event }) => event);
  const items: ILeadJourneyItem[] = [];
  let visit: ILeadJourneyItem | null = null;
  sorted.forEach((event) => {
    if (event.kind === 'page' && visit) {
      visit.pages.push(event);
      return;
    }
    const item: ILeadJourneyItem = { event, pages: [] };
    if (event.kind === 'visit') visit = item;
    items.push(item);
  });
  const days: ILeadJourneyDay[] = [];
  items.forEach((item) => {
    // День — по Варшаве, как время строк: событие в 23:30 по Варшаве не
    // уезжает в следующий день у человека в другом поясе.
    const day =
      warsawDayKey(item.event.at) ?? moment(item.event.at).format('YYYY-MM-DD');
    const last = days[days.length - 1];
    if (last && last.day === day) last.items.push(item);
    else days.push({ day, items: [item] });
  });
  return days;
};

/** Сводка пути над таймлайном. */
export interface ILeadJourneySummary {
  /** Канал первого события с каналом (визит, создание лида). */
  firstChannel: string | null;
  /** Визитов на сайт до создания лида (нет создания — все визиты). */
  visitsBeforeLead: number;
  /** Всего визитов. */
  visits: number;
  /** Целых дней от создания лида (или первого события) до первой продажи. */
  daysToSale: number | null;
  /** Есть ли продажа. */
  sold: boolean;
}

export const leadJourneySummary = (
  events: ReadonlyArray<ILeadJourneyEvent>,
): ILeadJourneySummary => {
  const sorted = [...events].sort(byTime);
  const created = sorted.find((event) => event.kind === 'lead_created');
  const createdAt = created ? moment(created.at).valueOf() : null;
  const visits = sorted.filter((event) => event.kind === 'visit');
  const sale = sorted.find((event) => event.kind === 'sale');
  const start = created ?? sorted[0];
  const daysToSale =
    sale && start
      ? Math.max(
          0,
          Math.floor(
            (moment(sale.at).valueOf() - moment(start.at).valueOf()) / 86400000,
          ),
        )
      : null;
  return {
    firstChannel: sorted.find((event) => event.channel)?.channel ?? null,
    visitsBeforeLead:
      createdAt === null
        ? visits.length
        : visits.filter((event) => moment(event.at).valueOf() < createdAt)
            .length,
    visits: visits.length,
    daysToSale,
    sold: Boolean(sale),
  };
};
